/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useState } from 'react';
import {
  StoreProvider,
  createLadybugStore,
  disposeLadybugStore,
} from './store';
import type { GraphStore } from './store';
import { setVaultApiBase } from './wiki/client';
import { ServerGraphStore } from './store/serverStore';
import { JobServiceProvider } from './job';
import App from './App';
import './styles/index.css';
import './App.css';

export interface OpenTraceAppProps {
  /** Shown in the version footer (bottom-right). Omit to hide the footer. */
  version?: string;
  /** Build timestamp shown in the version footer. Omit to hide. */
  buildTime?: string;
  /** Initial repository URL to index or open. */
  repoUrl?: string;
}

type StoreMode = 'local' | `server:${string}`;

function detectInitialMode(): StoreMode {
  try {
    const serverUrl = new URLSearchParams(window.location.search).get('server');
    if (serverUrl) {
      new URL(serverUrl); // validate
      return `server:${serverUrl}`;
    }
  } catch {
    /* invalid URL or SSR / non-browser */
  }
  return 'local';
}

function createStoreForMode(mode: StoreMode): GraphStore {
  if (mode.startsWith('server:')) {
    const url = mode.slice('server:'.length);
    // Store mode is the single source of truth for the server URL. Publish it
    // to the vault client too, which otherwise re-derives a base from
    // `?server=` and misses a runtime connect entirely.
    setVaultApiBase(url);
    return new ServerGraphStore(url);
  }
  setVaultApiBase(null);
  return createLadybugStore();
}

/**
 * Drop-in full OpenTrace application component.
 *
 * Includes all providers, styles, and the complete UI — graph canvas, chat
 * panel, settings drawer, and indexing.
 *
 * Usage:
 *   import { OpenTraceApp } from '@opentrace/opentrace/app';
 *   import '@opentrace/opentrace/style.css';
 *
 *   <OpenTraceApp />
 *
 * Note: requires Cross-Origin Isolation headers (COOP/COEP) for the
 * in-browser WASM graph store.
 */
export function OpenTraceApp({
  version,
  buildTime,
  repoUrl,
}: OpenTraceAppProps = {}) {
  const [mode, setMode] = useState<StoreMode>(detectInitialMode);
  const [store, setStore] = useState<GraphStore>(() =>
    createStoreForMode(mode),
  );

  const handleConnectServer = useCallback((serverUrl: string) => {
    // Leaving in-memory mode: tear down the local Web Worker + WASM DB so it
    // doesn't linger for the page lifetime. No-op if it was never created.
    disposeLadybugStore();
    const nextMode: StoreMode = `server:${serverUrl}`;
    setMode(nextMode);
    setStore(createStoreForMode(nextMode));
  }, []);

  return (
    <StoreProvider key={mode} store={store}>
      <JobServiceProvider>
        <App
          version={version}
          buildTime={buildTime}
          initialRepoUrl={repoUrl}
          onConnectServer={handleConnectServer}
          isServerMode={mode.startsWith('server:')}
        />
      </JobServiceProvider>
    </StoreProvider>
  );
}

export default OpenTraceApp;
